import React, { useState } from "react";
import {
  IonPage,
  IonContent,
  IonTextarea,
  IonButton,
  IonIcon,
  IonAlert,
} from "@ionic/react";
import {
  sparklesOutline,
  chevronDownOutline,
  shieldCheckmarkOutline,
} from "ionicons/icons";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getApp } from "firebase/app";
import "./Contribute.css";
import CategoryModal from "../components/CategoryModal";
import MasskaraLoading from "../components/MasskaraLoading";
import { track } from "../analytics";

const Contribute: React.FC = () => {
  const [category, setCategory] = useState<string>("");
  const [info, setInfo] = useState<string>("");
  const [contact, setContact] = useState<string>("");
  const [showModal, setShowModal] = useState(false);
  const [categoryLabel, setCategoryLabel] = useState<string>("");
  const [showLoading, setShowLoading] = useState(false);
  const [aiFeedback, setAiFeedback] = useState<string>("");
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [canSubmitAnyway, setCanSubmitAnyway] = useState(false);

  // 🔹 Main Submit Handler
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!category || !info.trim()) {
      alert("❌ Please select a category and enter your information.");
      return;
    }

    setShowLoading(true);

    try {
      // 1️⃣ Send to AI first for checking

      const aiResponse = await fetch("https://aicheckcontribution-lrenkrr2bq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category, info }),
      });
      
      const result = await aiResponse.json();
      setShowLoading(false);

      if (result.isGood) {
        // show alert that it's approved and save
        await saveToFirestore(true);
      } else {
        // show alert with result.feedback and ask to edit or submit anyway
        setAiFeedback(result.feedback);
        setShowFeedbackDialog(true);
        setCanSubmitAnyway(true);
      }

    } catch (err) {
      console.error("AI validation error:", err);
      setShowLoading(false);
      alert("AI validation failed. Please try again.");
    }
  };

  // 🔹 Save data to Firestore
  const saveToFirestore = async (passedReview: boolean) => {
    setShowLoading(true);
    try {
      const db = getFirestore(getApp());
      await addDoc(collection(db, "contributions"), {
        category,
        info: info.trim(),
        contact: contact || null,
        createdAt: serverTimestamp(),
        reviewPassed: passedReview
      });
      setShowLoading(false);
      track("contribution_submitted", { category, reviewPassed: passedReview });

      if (passedReview) {
        alert("Your contribution has been reviewed by AI and saved for manual verification. Thank you!");
      } else {
        alert("Thanks for your contribution! \n\nWe'll review it carefully. \n\nIf more details are needed, we may reach out. \n\nSubmissions with insufficient information may not be added, but you're welcome to resubmit with more details.")
      }

      // reset
      setCategory("");
      setCategoryLabel("");
      setInfo("");
      setContact("");
    } catch (error) {
      setShowLoading(false);
      alert("Oops! Something went wrong while saving. Please try again.");
      console.error("Error saving contribution:", error);
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding contribute-content web-centered">
        <div className="contribute-wrap">

          {/* Hero / intro */}
          <div className="contribute-hero">
            <div className="contribute-hero-icon">
              <IonIcon icon={sparklesOutline} />
            </div>
            <div className="contribute-hero-copy">
              <h1 className="contribute-hero-title">Help improve Bacolod AI</h1>
              <p className="contribute-hero-sub">
                Spotted a new place, a fare update, or something we got wrong?
                Share it — every contribution makes the guide smarter for everyone.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="contribute-form">

            {/* Category */}
            <div className="field">
              <label className="field-label">Choose a category</label>
              <button
                type="button"
                className={`field-select ${categoryLabel ? "has-value" : ""}`}
                onClick={() => setShowModal(true)}
              >
                <span>{categoryLabel ? categoryLabel : "Select a category"}</span>
                <IonIcon icon={chevronDownOutline} className="field-select-chevron" />
              </button>
              <input type="hidden" name="category" value={category} />
            </div>

            <CategoryModal
              isOpen={showModal}
              onClose={() => setTimeout(() => setShowModal(false), 100)}
              onSelect={(value, label) => {
                setCategory(value);
                setCategoryLabel(label);
              }}
            />

            {/* Info */}
            <div className="field">
              <label className="field-label">Your contribution</label>
              <div className="field-box">
                <IonTextarea
                  name="info"
                  className="field-textarea"
                  autoGrow={false}
                  value={info}
                  placeholder="Write all the details you'd like to share…"
                  onIonChange={(e) => setInfo(e.detail.value!)}
                />
              </div>
            </div>

            {/* Contact */}
            <div className="field">
              <label className="field-label">
                Contact information <span className="field-optional">optional</span>
              </label>
              <div className="field-box field-box--sm">
                <IonTextarea
                  name="contact"
                  className="field-textarea"
                  autoGrow={false}
                  value={contact}
                  placeholder="Email, phone, Facebook, etc."
                  onIonChange={(e) => setContact(e.detail.value!)}
                />
              </div>
            </div>

            {/* Note callout */}
            <div className="contribute-note-callout">
              <IonIcon icon={shieldCheckmarkOutline} />
              <span>
                Every contribution is reviewed by AI and verified manually before
                it’s added to our database.
              </span>
            </div>

            <IonButton
              mode="ios"
              expand="block"
              className="submit-btn"
              type="submit"
              disabled={showLoading}
            >
              {showLoading ? "Checking…" : "Submit contribution"}
            </IonButton>
          </form>
        </div>

        {/* Custom Loading */}
        <MasskaraLoading isOpen={showLoading} backdrop={true} />

        {/* Feedback Dialog */}
        <IonAlert
        isOpen={showFeedbackDialog}
        header="AI Feedback"
        message={aiFeedback}
        buttons={[
            {
            text: "Edit Submission",
            role: "cancel",
            handler: () => setShowFeedbackDialog(false),
            },
            ...(canSubmitAnyway
            ? [
                {
                    text: "Submit Anyway",
                    handler: async () => {
                    setShowFeedbackDialog(false);
                    await saveToFirestore(false);
                    },
                },
                ]
            : []),
        ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Contribute;
